import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { toast } from "@/hooks/use-toast";
import { api } from "@/api";
import { formatDateEU } from "@/components/shared/helpers";
import { User, FileText } from "lucide-react";

export default function AdminClients() {
  const [clients, setClients] = useState([]);
  const [nationalities, setNationalities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [nationalityFilter, setNationalityFilter] = useState("");
  const [total, setTotal] = useState(0);
  const [sendingEmail, setSendingEmail] = useState(null);

  const loadClients = async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (nationalityFilter) params.nationality = nationalityFilter;
      const res = await api.get("/admin/clients", { params });
      setClients(res.data.clients || []);
      setNationalities(res.data.nationalities || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      toast({ title: "Erreur", description: "Impossible de charger les clients.", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadClients(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const handleSearch = () => { loadClients(); };

  const exportClients = async () => {
    try {
      const res = await api.get("/admin/clients/export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `clients_export_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast({ title: "Export terminé", description: "Le fichier CSV a été téléchargé." });
    } catch (err) {
      toast({ title: "Erreur", description: "Export impossible.", variant: "destructive" });
    }
  };

  const sendEmail = async (clientEmail) => {
    setSendingEmail(clientEmail);
    try {
      await api.post("/admin/clients/send-email", null, { params: { email: clientEmail } });
      toast({ title: "Email envoyé", description: `Email de bienvenue envoyé à ${clientEmail}` });
      await loadClients();
    } catch (err) {
      toast({ title: "Envoi impossible", description: err?.response?.data?.detail || "Erreur lors de l'envoi", variant: "destructive" });
    } finally {
      setSendingEmail(null);
    }
  };

  return (
    <div data-testid="admin-clients-section" className="space-y-4">
      <Card data-testid="admin-clients-card" className="border-white/10 bg-white/[0.08]">
        <CardHeader>
          <CardTitle data-testid="admin-clients-title" className="text-slate-100 flex items-center gap-2">
            <User className="h-5 w-5" />
            Base de données clients
          </CardTitle>
          <div className="text-sm text-slate-300">Liste des locataires pour campagnes email (location ski, transferts aéroport/gare).</div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-1">
              <Label data-testid="admin-clients-search-label" className="text-slate-300">Recherche</Label>
              <Input data-testid="admin-clients-search-input" className="border-white/10 bg-white/[0.08] w-48" placeholder="Nom ou email..." value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === "Enter" && handleSearch()} />
            </div>
            <div className="space-y-1">
              <Label data-testid="admin-clients-nationality-label" className="text-slate-300">Nationalité</Label>
              <select data-testid="admin-clients-nationality-select" className="border border-white/10 bg-white/[0.08] text-slate-100 rounded px-3 py-2" value={nationalityFilter} onChange={(e) => setNationalityFilter(e.target.value)}>
                <option value="">Toutes</option>
                {nationalities.map((n) => (<option key={n} value={n}>{n}</option>))}
              </select>
            </div>
            <Button data-testid="admin-clients-search-btn" variant="outline" className="border-white/10 bg-violet-600 hover:bg-violet-700 text-white" onClick={handleSearch}>Filtrer</Button>
            <Button data-testid="admin-clients-export-btn" variant="outline" className="border-white/10 bg-emerald-600 hover:bg-emerald-700 text-white" onClick={exportClients}>
              <FileText className="h-4 w-4 mr-1" /> Exporter CSV
            </Button>
          </div>
          <div className="text-sm text-slate-300">{total} client(s) trouvé(s)</div>
          <div className="rounded-xl border border-white/10 bg-white/[0.08]">
            <Table data-testid="admin-clients-table">
              <TableHeader>
                <TableRow>
                  <TableHead data-testid="admin-clients-col-name">Nom</TableHead>
                  <TableHead data-testid="admin-clients-col-email">Email</TableHead>
                  <TableHead data-testid="admin-clients-col-nationality">Nationalité</TableHead>
                  <TableHead data-testid="admin-clients-col-stays">Séjours</TableHead>
                  <TableHead data-testid="admin-clients-col-last">Dernier séjour</TableHead>
                  <TableHead data-testid="admin-clients-col-email-status">Email</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={6} className="py-8 text-center text-slate-300">Chargement...</TableCell></TableRow>
                ) : clients.length === 0 ? (
                  <TableRow><TableCell colSpan={6} className="py-8 text-center text-slate-300">Aucun client trouvé.</TableCell></TableRow>
                ) : (
                  clients.map((client, idx) => (
                    <TableRow key={client.email || idx} data-testid={`admin-client-row-${idx}`}>
                      <TableCell data-testid={`admin-client-name-${idx}`} className="font-medium text-slate-100">{client.name || "-"}</TableCell>
                      <TableCell data-testid={`admin-client-email-${idx}`} className="text-slate-200"><a href={`mailto:${client.email}`} className="text-violet-400 hover:underline">{client.email}</a></TableCell>
                      <TableCell data-testid={`admin-client-nationality-${idx}`} className="text-slate-300">{client.nationality || "-"}</TableCell>
                      <TableCell data-testid={`admin-client-stays-${idx}`} className="text-slate-300">{client.stays_count}</TableCell>
                      <TableCell data-testid={`admin-client-last-${idx}`} className="text-slate-300">{formatDateEU(client.last_stay)}</TableCell>
                      <TableCell data-testid={`admin-client-email-action-${idx}`}>
                        {client.email_sent ? (
                          <span className="inline-flex items-center px-2 py-1 text-xs text-emerald-400 bg-emerald-500/10 rounded">✓ Envoyé</span>
                        ) : !client.has_consent ? (
                          <span className="inline-flex items-center px-2 py-1 text-xs text-slate-400 bg-slate-500/10 rounded" title="Le propriétaire n'a pas activé le consentement marketing">Pas de consentement</span>
                        ) : (
                          <Button data-testid={`admin-client-send-email-${idx}`} variant="outline" size="sm" className="border-violet-500/30 bg-violet-500/10 text-violet-200 hover:bg-violet-500/20" onClick={() => sendEmail(client.email)} disabled={sendingEmail === client.email}>
                            {sendingEmail === client.email ? "Envoi..." : "✉️ Envoyer"}
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
